import { toast } from 'sonner';
import { useState } from 'react';
import { useMutation } from '@apollo/client/react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import { EscrowInvitationForm, type IEscrowInvitationForm } from '@/components/Escrow/Invitation';
import { EscrowInvitationUpdateDocument } from 'src/graphql/actions/escrow/escrowInvitationUpdate.mutation';
import { getApiError, ClientError } from 'src/utils/errors';
import { Button } from '@/components/ui/button';

import { useEscrowInvitationContext } from '../model';

const EditInvitationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { escrowInvitation, invitePartyType } = useEscrowInvitationContext();
  const [updateInvitation] = useMutation(EscrowInvitationUpdateDocument);

  const onSubmit = async ({ companyName, userEmail, userName }: IEscrowInvitationForm) => {
    try {
      const { data } = await updateInvitation({
        variables: {
          escrowInvitationId: escrowInvitation!.id,
          escrowInvitationParams: {
            companyName,
            userEmail,
            userName,
          },
        },
      });

      if (data?.escrowInvitationUpdate?.errors?.length) {
        throw new ClientError(data.escrowInvitationUpdate.errors[0]);
      } else {
        toast.success('Saved!');
        setIsOpen(false);
      }
    } catch (e: unknown) {
      getApiError(e, toast.error);
    }
  };

  const onCancel = () => setIsOpen(false);
  const defaultValues = {
    userEmail: escrowInvitation?.userEmail || '',
    userName: escrowInvitation?.userName || '',
    companyName: escrowInvitation?.companyName || '',
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Edit</Button>
      </DialogTrigger>
      <DialogContent className="md:max-w-[374px] sm:max-w-[374px]">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">
            Edit the {invitePartyType} organization & their primary contact
          </DialogTitle>
          <DialogDescription className="sr-only">Edit contact</DialogDescription>
        </DialogHeader>
        <EscrowInvitationForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues} />
      </DialogContent>
    </Dialog>
  );
};

export default EditInvitationDialog;
